/* eslint-disable unicode-bom */
import React, { useMemo, useState, useEffect } from 'react';
import { Calendar, Views, momentLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import moment from 'moment';
import 'moment/locale/tr';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './calendarOverrides.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';

const DAY_MAP = {
  'Pazartesi': 1,
  'Salı': 2,
  'Çarşamba': 3,
  'Perşembe': 4,
  'Cuma': 5,
  'Cumartesi': 6,
  'Pazar': 0,
};

// Build events from schedule JSON using current week
function scheduleToEvents(schedule, baseDate = new Date(), overridesByDate = {}) {
  const startOfWeek = moment(baseDate).startOf('isoWeek'); // Monday as 1
  const events = [];
  const processedDates = new Set();
  const displayTitle = (subject, teacher) => {
    const s = String(subject || '').trim();
    const t = String(teacher || '').trim();
    return t ? `${s} (${t})` : s;
  };
  Object.entries(schedule || {}).forEach(([dayName, lessons]) => {
    const dow = DAY_MAP[dayName] ?? 1;
    const base = startOfWeek.clone().day(dow);
    const dateIso = base.format('YYYY-MM-DD');
    const ov = overridesByDate[dateIso];
    if (ov && ov.cancelAll) {
      const allDayStart = base.clone().startOf('day').toDate();
      const allDayEnd = base.clone().endOf('day').toDate();
      const rawNote = String(ov.note || '').trim();
      const noteTitle = rawNote || '—';
      events.push({ id: `${dayName}-holiday`, title: noteTitle, start: allDayStart, end: allDayEnd, meta: { holiday: true, dateIso, note: ov.note } });
      processedDates.add(dateIso);
      return;
    }
  (lessons || []).forEach((l, idx) => {
      const [sh, sm] = String(l.start || '00:00').split(':').map(Number);
      const [eh, em] = String(l.end || '00:00').split(':').map(Number);
      const start = base.clone().hour(sh).minute(sm).toDate();
      const end = base.clone().hour(eh).minute(em).toDate();
      events.push({
        id: `${dayName}-${idx}`,
        title: displayTitle(l.subject, l.teacher),
        start,
        end,
    meta: { dayName, idx, teacher: l.teacher || '' },
      });
    });
    if (ov && Array.isArray(ov.lessons)) {
      // If lessons array is empty, interpret as full-day cancellation (holiday) even if cancelAll not explicitly set
      if (!ov.lessons.length) {
        const allDayStart = base.clone().startOf('day').toDate();
        const allDayEnd = base.clone().endOf('day').toDate();
        const rawNote = String(ov.note || '').trim();
        const noteTitle = rawNote || '—';
        events.push({ id: `${dayName}-holiday-empty-${dateIso}`, title: noteTitle, start: allDayStart, end: allDayEnd, meta: { holiday: true, dateIso, note: ov.note } });
        processedDates.add(dateIso);
        return;
      }
      const startOfDay = base.clone().startOf('day');
      const endOfDay = base.clone().endOf('day');
      for (let i = events.length - 1; i >= 0; i--) {
        const ev = events[i];
        if (moment(ev.start).isBetween(startOfDay, endOfDay, undefined, '[]')) {
          events.splice(i, 1);
        }
      }
      ov.lessons.forEach((l, idx) => {
        const [sh, sm] = String(l.start || '00:00').split(':').map(Number);
        const [eh, em] = String(l.end || '00:00').split(':').map(Number);
        const start = base.clone().hour(sh).minute(sm).toDate();
        const end = base.clone().hour(eh).minute(em).toDate();
        events.push({ id: `${dayName}-ov-${idx}`, title: displayTitle(l.subject, l.teacher), start, end, meta: { dayName, idx, teacher: l.teacher || '', override: true } });
      });
      processedDates.add(dateIso);
    }
  });
  // Add overrides for days without any schedule entries processed above
  for (let i = 0; i < 7; i++) {
    const base = startOfWeek.clone().add(i, 'days');
    const dateIso = base.format('YYYY-MM-DD');
    if (processedDates.has(dateIso)) continue;
    const ov = overridesByDate[dateIso];
    if (!ov) continue;
    if (ov.cancelAll) {
      const allDayStart = base.clone().startOf('day').toDate();
      const allDayEnd = base.clone().endOf('day').toDate();
      const rawNote = String(ov.note || '').trim();
      const noteTitle = rawNote || '—';
      events.push({ id: `holiday-${dateIso}`, title: noteTitle, start: allDayStart, end: allDayEnd, meta: { holiday: true, dateIso, note: ov.note } });
      continue;
    }
    if (Array.isArray(ov.lessons)) {
      if (!ov.lessons.length) {
        const allDayStart = base.clone().startOf('day').toDate();
        const allDayEnd = base.clone().endOf('day').toDate();
        const rawNote = String(ov.note || '').trim();
        const noteTitle = rawNote || '—';
        events.push({ id: `holiday-empty-${dateIso}`, title: noteTitle, start: allDayStart, end: allDayEnd, meta: { holiday: true, dateIso, note: ov.note } });
        continue;
      }
      ov.lessons.forEach((l, idx) => {
        const [sh, sm] = String(l.start || '00:00').split(':').map(Number);
        const [eh, em] = String(l.end || '00:00').split(':').map(Number);
        const start = base.clone().hour(sh).minute(sm).toDate();
        const end = base.clone().hour(eh).minute(em).toDate();
    events.push({ id: `ov-${dateIso}-${idx}`, title: displayTitle(l.subject, l.teacher), start, end, meta: { idx, teacher: l.teacher || '', override: true } });
      });
    }
  }
  return events;
}

function eventsToSchedule(events) {
  const byDay = {};
  events.forEach((ev) => {
    const dayName = moment(ev.start).format('dddd');
    const trDay = Object.keys(DAY_MAP).find((k) => moment().day(DAY_MAP[k]).format('dddd') === dayName) || dayName;
    const start = moment(ev.start).format('HH:mm');
    const end = moment(ev.end).format('HH:mm');
    if (!byDay[trDay]) byDay[trDay] = [];
    const subjectOnly = String(ev.title || '').replace(/\s*\([^)]*\)\s*$/, '').trim();
    const item = { start, end, subject: subjectOnly };
    const t = ev?.meta?.teacher;
    if (t && String(t).trim()) item.teacher = String(t).trim();
    byDay[trDay].push(item);
  });
  // sort by start time within a day
  Object.keys(byDay).forEach((d) => {
    byDay[d].sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));
  });
  return byDay;
}
function eventsToByDate(events) {
  const by = {};
  events.forEach((ev) => {
    const key = moment(ev.start).format('YYYY-MM-DD');
    if (!by[key]) by[key] = [];
    const subjectOnly = String(ev.title || '').replace(/\s*\([^)]*\)\s*$/, '').trim();
    const item = { start: moment(ev.start).format('HH:mm'), end: moment(ev.end).format('HH:mm'), subject: subjectOnly };
    const t = ev?.meta?.teacher;
    if (t && String(t).trim()) item.teacher = String(t).trim();
    by[key].push(item);
  });
  Object.keys(by).forEach((k) => by[k].sort((a,b)=> (a.start<b.start?-1:a.start>b.start?1:0)));
  return by;
}

export default function CalendarView({ schedule, onChange, onChangeByDate, overridesByDate = {}, defaultMinutes = 40, onRemoveHoliday, onWeekChange, lessons = [], teachers = [], calendarView = 'week', classColor = null, eventsOverride = null, readOnly = false, colorMap = {}, onRemoveClassHoliday, onRemoveGlobalHoliday, lockedClass = '', lockedTeacher = '', defaultSubject = '', defaultTeacher = '', allowOneOff = true }) {
  const localizer = useMemo(() => momentLocalizer(moment), []);
  // Ensure week starts Monday (TR locale)
  useEffect(() => {
    try {
      moment.updateLocale('tr', { week: { dow: 1, doy: 4 } });
      moment.locale('tr');
    } catch {}
  }, []);
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [view, setView] = useState(() => calendarView === 'day' ? Views.DAY : Views.WEEK);
  const noon = useMemo(() => new Date(1970, 0, 1, 12, 0, 0), []);
  const generatedEvents = useMemo(() => scheduleToEvents(schedule, currentDate, overridesByDate), [schedule, currentDate, overridesByDate]);
  const events = eventsOverride || generatedEvents;
  const DnDCalendar = useMemo(() => withDragAndDrop(Calendar), []);
  const trMessages = useMemo(() => ({
    today: 'Bugün',
    previous: 'Geri',
    next: 'İleri',
    month: 'Ay',
    week: 'Hafta',
    day: 'Gün',
    agenda: 'Ajanda',
    date: 'Tarih',
    time: 'Saat',
    event: 'Etkinlik',
    noEventsInRange: 'Bu aralıkta etkinlik yok',
    showMore: (total) => `+${total} daha`,
  }), []);

  // Force 24-hour time formats across the calendar
  const formats = useMemo(() => ({
    // Show both start and end times inside event blocks
    eventTimeRangeFormat: ({ start, end }) => `${moment(start).format('HH:mm')} - ${moment(end).format('HH:mm')}`,
    agendaTimeRangeFormat: ({ start, end }) => `${moment(start).format('HH:mm')} - ${moment(end).format('HH:mm')}`,
    selectRangeFormat: ({ start, end }) => `${moment(start).format('DD MMM ddd HH:mm')} - ${moment(end).format('DD MMM ddd HH:mm')}`,
    timeGutterFormat: (date) => moment(date).format('HH:mm'),
  }), []);

  // Only update internal view when the prop changes (do not override user toggles)
  useEffect(() => {
    const next = calendarView === 'day' ? Views.DAY : Views.WEEK;
    setView(next);
  }, [calendarView]);

  // Dialog state for add/edit and holiday confirm
  const [dialog, setDialog] = useState({ open: false, mode: 'add', title: '', teacher: '', slot: null, event: null, holidayDateIso: null, oneOff: false, startStr: '', endStr: '' });
  // Normalize time helper (accepts 9, 930, 0930, 9:30)
  const normalizeTime = (raw) => {
    const s = String(raw || '').trim();
    if (!s) return s;
    if (/^\d{1,2}$/.test(s)) {
      const hh = parseInt(s, 10);
      if (hh >= 0 && hh <= 23) return `${String(hh).padStart(2,'0')}:00`;
    }
    if (/^\d{3}$/.test(s)) {
      const hh = Math.floor(parseInt(s, 10) / 100);
      const mm = parseInt(s, 10) % 100;
      if (hh >= 0 && hh <= 23 && mm >= 0 && mm <= 59) return `${String(hh).padStart(2,'0')}:${String(mm).padStart(2,'0')}`;
    }
    if (/^\d{4}$/.test(s)) {
      const hh = parseInt(s.slice(0,2), 10);
      const mm = parseInt(s.slice(2), 10);
      if (hh >= 0 && hh <= 23 && mm >= 0 && mm <= 59) return `${String(hh).padStart(2,'0')}:${String(mm).padStart(2,'0')}`;
    }
    if (/^\d{1,2}:\d{2}$/.test(s)) return s;
    return s;
  };
  const lessonOptions = useMemo(() => Array.from(new Set([
    ...(lessons || []).map((lesson) => (typeof lesson === 'string' ? lesson : lesson?.name)).filter(Boolean),
  ].filter(Boolean))), [lessons]);
  const previousLesson = dialog.mode === 'edit'
    ? String(dialog.event?.title || '').replace(/\s*\([^)]*\)\s*$/, '').trim()
    : '';
  const dialogLessonOptions = previousLesson && !lessonOptions.includes(previousLesson)
    ? [...lessonOptions, previousLesson]
    : lessonOptions;
  const canSaveDialogLesson = dialogLessonOptions.includes(String(dialog.title || '').trim());
  const teacherOptions = useMemo(() => (teachers || [])
    .map((t) => (typeof t === 'string' ? t : `${t?.name || ''} ${t?.surname || ''}`.trim()))
    .concat(String(lockedTeacher || '').trim())
    .filter(Boolean)
    .filter((teacher, index, list) => list.indexOf(teacher) === index), [teachers, lockedTeacher]);
  const isLockedTeacherEvent = (event) => {
    const expected = String(lockedTeacher || '').trim().toLocaleLowerCase('tr-TR');
    if (!expected) return true;
    return String(event?.meta?.teacher || '').trim().toLocaleLowerCase('tr-TR') === expected;
  };
  const canEditEvent = (event) => !event?.meta?.holiday && !event?.meta?.override && isLockedTeacherEvent(event);
  const findSubjectForTeacher = (teacherName) => {
    const normalizedName = String(teacherName || '').trim().toLocaleLowerCase('tr-TR');
    const selectedTeacher = (teachers || []).find((item) => (
      `${item?.name || ''} ${item?.surname || ''}`.trim().toLocaleLowerCase('tr-TR') === normalizedName
    ));
    const candidate = String(selectedTeacher?.primary_lesson || selectedTeacher?.branch || '').trim();
    return lessonOptions.includes(candidate) ? candidate : '';
  };

  const closeDialog = () => setDialog((d) => ({ ...d, open: false }));

  const openAdd = (slot) => {
    const startStr = moment(slot?.start).format('HH:mm');
    const endStr = moment(slot?.start).add(defaultMinutes, 'minutes').format('HH:mm');
    const title = lessonOptions.includes(String(defaultSubject || '').trim()) ? String(defaultSubject).trim() : '';
    setDialog({ open: true, mode: 'add', title, teacher: String(defaultTeacher || '').trim(), slot, event: null, holidayDateIso: null, oneOff: false, startStr, endStr });
  };
  const openEdit = (event) => {
    if (event?.meta?.holiday) {
      // Single-class specific holiday removal (legacy) retained if handler provided
      if (!readOnly && onChangeByDate && typeof onRemoveHoliday === 'function') {
        setDialog({ open: true, mode: 'holiday', title: '', teacher: '', slot: null, event: null, holidayDateIso: event?.meta?.dateIso });
        return;
      }
      // Global (ALL) holiday removal allowed only if global handler provided
      if (readOnly && typeof onRemoveGlobalHoliday === 'function') {
        setDialog({ open: true, mode: 'globalHoliday', title: '', teacher: '', slot: null, event: null, holidayDateIso: event?.meta?.dateIso });
        return;
      }
    }
    {
      const startStr = moment(event?.start).format('HH:mm');
      const endStr = moment(event?.end).format('HH:mm');
      // Prefill only the lesson name (strip optional teacher in parentheses)
      const subjectOnly = String(event?.title || '').replace(/\s*\([^)]*\)\s*$/, '').trim();
      setDialog({
        open: true,
        mode: 'edit',
        title: subjectOnly,
        teacher: event?.meta?.teacher || '',
        slot: null,
        event,
        holidayDateIso: null,
        oneOff: false,
        startStr,
        endStr,
      });
    }
  };

  const sameEventTime = (a, b) => {
    try {
      return new Date(a.start).getTime() === new Date(b.start).getTime() && new Date(a.end).getTime() === new Date(b.end).getTime();
    } catch (e) { return false; }
  };

  const saveAdd = () => {
  const title = String(dialog.title || '').trim();
  if (!title || !lessonOptions.includes(title)) { closeDialog(); return; }
    const base = dialog?.slot?.start || new Date();
    const sStr = normalizeTime(dialog.startStr);
    const eStr = normalizeTime(dialog.endStr);
    const [sh, sm] = String(sStr || '').split(':').map(Number);
    let [eh, em] = String(eStr || '').split(':').map(Number);
    let start = moment(base).hour(sh || 0).minute(sm || 0).second(0).millisecond(0).toDate();
    let end;
    if (Number.isFinite(eh) && Number.isFinite(em)) {
      end = moment(base).hour(eh).minute(em).second(0).millisecond(0).toDate();
    } else {
      end = moment(start).add(defaultMinutes, 'minutes').toDate();
    }
  const newEvent = { title, start, end, meta: { teacher: String(dialog.teacher || '').trim() } };
    const nextEvents = [...events, newEvent];
    if (dialog.oneOff && typeof onChangeByDate === 'function') {
      onChangeByDate(eventsToByDate(nextEvents));
    } else {
      onChange && onChange(eventsToSchedule(nextEvents));
    }
    closeDialog();
  };

  const saveEdit = () => {
  const nextTitle = String(dialog.title || '').trim();
  if (!nextTitle) {
      const remaining = events.filter((ev) => !sameEventTime(ev, dialog.event));
      if (onChangeByDate) {
        const by = eventsToByDate(remaining);
        const dateIso = moment(dialog.event.start).format('YYYY-MM-DD');
        if (!by[dateIso]) by[dateIso] = [];
        onChangeByDate(by);
      } else { onChange && onChange(eventsToSchedule(remaining)); }
      closeDialog();
      return;
    }
  if (!canSaveDialogLesson) { return; }
  const sStr = normalizeTime(dialog.startStr);
  const eStr = normalizeTime(dialog.endStr);
  const [sh, sm] = String(sStr || '').split(':').map(Number);
  let [eh, em] = String(eStr || '').split(':').map(Number);
  const base = dialog?.event?.start || new Date();
  const newStart = moment(base).hour(sh || 0).minute(sm || 0).second(0).millisecond(0).toDate();
  let newEnd;
  if (Number.isFinite(eh) && Number.isFinite(em)) {
    newEnd = moment(base).hour(eh).minute(em).second(0).millisecond(0).toDate();
  } else {
    newEnd = moment(newStart).add(defaultMinutes, 'minutes').toDate();
  }
  const updated = events.map((ev) => (sameEventTime(ev, dialog.event) ? { ...ev, title: nextTitle, start: newStart, end: newEnd, meta: { ...(ev.meta || {}), teacher: String(dialog.teacher || '').trim() } } : ev));
  if (dialog.oneOff && typeof onChangeByDate === 'function') {
      const by = eventsToByDate(updated);
      const dateIso = moment(dialog.event.start).format('YYYY-MM-DD');
      if (!by[dateIso]) by[dateIso] = [];
      onChangeByDate(by);
    } else { onChange && onChange(eventsToSchedule(updated)); }
    closeDialog();
  };

  const deleteEdit = () => {
    const remaining = events.filter((ev) => !sameEventTime(ev, dialog.event));
  if (dialog.oneOff && typeof onChangeByDate === 'function') {
      const by = eventsToByDate(remaining);
      const dateIso = moment(dialog.event.start).format('YYYY-MM-DD');
      if (!by[dateIso]) by[dateIso] = [];
      onChangeByDate(by);
    } else { onChange && onChange(eventsToSchedule(remaining)); }
    closeDialog();
  };

  const confirmRemoveHoliday = () => {
    if (dialog.mode === 'holiday') {
      if (dialog.holidayDateIso && typeof onRemoveHoliday === 'function') {
        onRemoveHoliday(dialog.holidayDateIso);
      }
    } else if (dialog.mode === 'globalHoliday') {
      if (dialog.holidayDateIso && typeof onRemoveGlobalHoliday === 'function') {
        onRemoveGlobalHoliday(dialog.holidayDateIso);
      }
    }
    closeDialog();
  };

  const eventPropGetter = (event) => {
    if (event && event.meta && event.meta.holiday) {
      return { style: { backgroundColor: '#ffebee', borderColor: '#e57373', color: '#c62828' } };
    }
    if (event?.meta?.className && colorMap && colorMap[event.meta.className]) {
      const col = colorMap[event.meta.className];
      return { style: { backgroundColor: col, borderColor: col } };
    }
    if (classColor) {
      return { style: { backgroundColor: String(classColor), borderColor: String(classColor) } };
    }
    return {};
  };

  return (
    <div style={{ height: 600 }}>
      <DnDCalendar
        localizer={localizer}
        culture="tr"
        messages={trMessages}
  formats={formats}
        events={events}
        defaultView={view}
        views={[Views.DAY, Views.WEEK]}
        view={view}
        onView={(v) => setView(v)}
        date={currentDate}
        onNavigate={(date) => { setCurrentDate(date); onWeekChange && onWeekChange(date); }}
  step={5}
  timeslots={12}
        scrollToTime={noon}
        selectable={!readOnly}
        resizable={!readOnly}
        eventPropGetter={eventPropGetter}
        onSelectSlot={(slot) => { if (!readOnly) openAdd(slot); }}
        onSelectEvent={(event) => { if (!readOnly && canEditEvent(event)) openEdit(event); }}
        onEventDrop={({ event, start, end }) => {
          if (readOnly || !canEditEvent(event)) return;
          const moved = events.map((ev) => (sameEventTime(ev, event) ? { ...ev, start, end } : ev));
          onChange && onChange(eventsToSchedule(moved));
        }}
        onEventResize={({ event, start, end }) => {
          if (readOnly || !canEditEvent(event)) return;
          const resized = events.map((ev) => (sameEventTime(ev, event) ? { ...ev, start, end } : ev));
          onChange && onChange(eventsToSchedule(resized));
        }}
        style={{ minHeight: 500 }}
      />

      {/* Add/Edit/Holiday Dialog */}
  {(!readOnly || (readOnly && dialog.mode === 'globalHoliday')) && (
  <Dialog open={dialog.open} onClose={closeDialog} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
        {dialog.mode === 'holiday' || dialog.mode === 'globalHoliday' ? (
          <>
            <DialogTitle>{dialog.mode === 'globalHoliday' ? 'Tüm Şubeler Tatilini Kaldır' : 'Tatili Kaldır'}</DialogTitle>
            <DialogContent>
              <Typography>Seçili gündeki tatil kaydını kaldırmak istiyor musunuz?</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDialog}>Vazgeç</Button>
              <Button color="error" variant="contained" onClick={confirmRemoveHoliday}>Kaldır</Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle>{dialog.mode === 'add' ? 'Ders Ekle' : 'Dersi Düzenle'}</DialogTitle>
            <DialogContent dividers>
              {lockedClass && (
                <TextField
                  label="Sınıf"
                  value={lockedClass}
                  disabled
                  fullWidth
                  size="small"
                  margin="dense"
                />
              )}
              <Autocomplete
                options={dialogLessonOptions}
                value={dialogLessonOptions.includes(dialog.title) ? dialog.title : null}
                onChange={(_, v) => {
                  const next = v || '';
                  setDialog((d) => {
                    return { ...d, title: next };
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    autoFocus
                    label="Ders adı"
                    fullWidth
                    size="small"
                    placeholder="Örn. Matematik"
                    margin="dense"
                  />
                )}
              />
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <TextField
                  label="Başlangıç (HH:MM)"
                  type="text"
                  size="small"
                  inputMode="numeric"
                  placeholder="HH:MM"
                  value={dialog.startStr || ''}
                  onChange={(e) => setDialog((d) => ({ ...d, startStr: e.target.value }))}
                  onBlur={(e) => setDialog((d) => ({ ...d, startStr: normalizeTime(e.target.value) }))}
                />
                <TextField
                  label="Bitiş (HH:MM)"
                  type="text"
                  size="small"
                  inputMode="numeric"
                  placeholder="HH:MM"
                  value={dialog.endStr || ''}
                  onChange={(e) => setDialog((d) => ({ ...d, endStr: e.target.value }))}
                  onBlur={(e) => setDialog((d) => ({ ...d, endStr: normalizeTime(e.target.value) }))}
                />
              </Stack>
              <Autocomplete
                options={teacherOptions}
                value={teacherOptions.includes(dialog.teacher) ? dialog.teacher : null}
                onChange={(_, v) => setDialog((d) => {
                  const teacher = v || '';
                  const subject = findSubjectForTeacher(teacher);
                  return { ...d, teacher, title: subject || d.title };
                })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Öğretmen (opsiyonel)"
                    fullWidth
                    size="small"
                    placeholder="Örn. Ali Yılmaz"
                    margin="dense"
                  />
                )}
              />
              {allowOneOff && (
                <FormControlLabel
                  control={<Checkbox checked={!!dialog.oneOff} onChange={(e) => setDialog((d) => ({ ...d, oneOff: e.target.checked }))} />}
                  label="Tek Seferlik"
                />
              )}
              {dialog.mode === 'add' && (
                <Typography variant="caption" color="text.secondary">Süre varsayılan olarak {defaultMinutes} dakika olacaktır.</Typography>
              )}
            </DialogContent>
            <DialogActions>
              {dialog.mode === 'edit' && (
                <Button color="error" onClick={deleteEdit}>Sil</Button>
              )}
              <Button onClick={closeDialog}>Vazgeç</Button>
              <Button variant="contained" disabled={!canSaveDialogLesson} onClick={dialog.mode === 'add' ? saveAdd : saveEdit}>Kaydet</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
  )}
    </div>
  );
}









